import { create } from 'zustand';

import { OptionType } from '@evinced-private/ui-common';

import { getWebSdkTestLabels } from 'src/apps/web-sdks/services/WebSdkDashboardService';
import { WEB_SDK_TEST_LABELS } from 'src/common/providers/reactQueryProvider/QueryKeys';
import { queryClient } from 'src/common/providers/reactQueryProvider/ReactQueryProvider';
import { Logger } from 'src/common/services/Logger';

interface WebSDKFiltersState {
	// Data
	allLabels: OptionType[];

	// Select All state
	isSelectAllLabels: boolean;
	labelsSearchTerm: string;

	// Loading state
	isLoading: boolean;
	isLoaded: boolean;
	error: Error | null;

	// Actions
	loadLabels: () => Promise<void>;
	setSelectAllLabels: (isSelectAll: boolean) => void;
	setLabelsSearchTerm: (searchTerm: string) => void;
	getFilteredLabels: () => OptionType[];
	reset: () => void;
}

const initialState = {
	allLabels: [],
	isSelectAllLabels: false,
	labelsSearchTerm: '',
	isLoading: false,
	isLoaded: false,
	error: null
};

export const useWebSDKFiltersStore = create<WebSDKFiltersState>((set, get) => ({
	...initialState,

	loadLabels: async () => {
		const { isLoading, isLoaded } = get();

		// Skip if already loading or loaded
		if (isLoading || isLoaded) {
			return;
		}

		set({ isLoading: true, error: null });

		try {
			const allLabels = await queryClient.fetchQuery({
				queryKey: [WEB_SDK_TEST_LABELS],
				queryFn: getWebSdkTestLabels
			});

			set({
				allLabels,
				isLoading: false,
				isLoaded: true
			});
		} catch (error) {
			Logger.error('WebSDKFiltersStore -> loadLabels failed:', error);
			set({
				isLoading: false,
				error: error instanceof Error ? error : new Error('Failed to load labels')
			});
		}
	},

	setSelectAllLabels: (isSelectAll: boolean) => {
		set({ isSelectAllLabels: isSelectAll });
	},

	setLabelsSearchTerm: (searchTerm: string) => {
		set({ labelsSearchTerm: searchTerm });
	},

	getFilteredLabels: () => {
		const { allLabels, labelsSearchTerm } = get();
		if (!labelsSearchTerm.trim()) {
			return allLabels;
		}
		const searchLower = labelsSearchTerm.toLowerCase();
		return allLabels.filter((opt) => opt.label.toLowerCase().includes(searchLower));
	},

	reset: () => set(initialState)
}));
